"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Icon } from "@/components/formatters/MXPIcon";
import type { FilterGroup, FilterOption } from "./types";
import type { FilterableCardsLabelsFields } from "@/queries/fragments/filterableCardsLabelsFragment";
interface FilterSidebarProps {
  filterGroups: FilterGroup[];
  activeFilters: Record<string, string[]>;
  onFilterChange: (groupId: string, values: string[]) => void;
  onClearAll: () => void;
  labels?: FilterableCardsLabelsFields | null;
}
const CHECKBOX_BASE = "min-w-[18px] min-h-[18px] w-[18px] h-[18px] cursor-pointer appearance-none rounded border border-gray-m4 t2-dark:border-gray-m5 bg-transparent pointer-events-none shrink-0";
const checkedStyle: React.CSSProperties = {
  backgroundColor: "#0063A3",
  borderColor: "#0063A3",
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8l3 3 5-6'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundSize: "70%"
};
const FilterCheckbox = t0 => {
  const $ = _c(19);
  const {
    option,
    checked,
    onToggle,
    indentLevel: t1
  } = t0;
  const indentLevel = t1 === undefined ? 0 : t1;
  let t2;
  if ($[0] !== indentLevel) {
    t2 = indentLevel > 0 ? {
      paddingLeft: `${indentLevel * 20}px`
    } : undefined;
    $[0] = indentLevel;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== onToggle || $[3] !== option.value) {
    t3 = () => onToggle(option.value);
    $[2] = onToggle;
    $[3] = option.value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = checked ? checkedStyle : undefined;
  let t5;
  if ($[5] !== checked || $[6] !== t4) {
    t5 = <input type="checkbox" className={CHECKBOX_BASE} style={t4} checked={checked} readOnly={true} />;
    $[5] = checked;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = checked ? "text-blue-trmb t2-dark:text-yellow-m10 font-semibold" : "";
  let t7;
  if ($[8] !== option.label || $[9] !== t6) {
    t7 = <span className={t6}>{option.label}</span>;
    $[8] = option.label;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== option.count) {
    t8 = option.count !== undefined && <span className="text-gray-m4 t2-dark:text-gray-m5 text-xs ml-auto">{option.count}</span>;
    $[11] = option.count;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t5 || $[16] !== t7 || $[17] !== t8) {
    t9 = <li className="flex items-center gap-3 py-2 cursor-pointer text-gray-m7 t2-dark:text-gray-m3 text-sm leading-5 hover:text-gray-m9 t2-dark:hover:text-gray-m9" style={t2} onClick={t3}>{t5}{t7}{t8}</li>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};
const FilterGroupSection = t0 => {
  const $ = _c(23);
  const {
    group,
    activeValues,
    onFilterChange
  } = t0;
  const [isOpen, setIsOpen] = useState(!group.isCollapsedByDefault);
  let t1;
  if ($[0] !== activeValues || $[1] !== group.id || $[2] !== onFilterChange) {
    t1 = value => {
      const next = activeValues.includes(value) ? activeValues.filter(v => v !== value) : [...activeValues, value];
      onFilterChange(group.id, next);
    };
    $[0] = activeValues;
    $[1] = group.id;
    $[2] = onFilterChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const toggleValue = t1;
  let t2;
  if ($[4] !== isOpen) {
    t2 = () => setIsOpen(!isOpen);
    $[4] = isOpen;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== group.label) {
    t3 = <span className="text-sm font-bold text-gray-m9 tracking-wide">{group.label}</span>;
    $[6] = group.label;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = isOpen ? "270" : "90";
  let t5;
  if ($[8] !== t4) {
    t5 = <Icon type="t2u-chevron-right" size={12} rotateDeg={t4} />;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isOpen || $[11] !== t2 || $[12] !== t3 || $[13] !== t5) {
    t6 = <button className="flex items-center justify-between w-full text-left cursor-pointer bg-transparent border-none p-0" onClick={t2} aria-expanded={isOpen}>{t3}{t5}</button>;
    $[10] = isOpen;
    $[11] = t2;
    $[12] = t3;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== activeValues || $[16] !== group.options || $[17] !== isOpen || $[18] !== toggleValue) {
    t7 = isOpen && <ul className="list-none m-0 p-0 mt-2">{group.options.map(option => <FilterCheckboxTree key={option.value} option={option} activeValues={activeValues} onToggle={toggleValue} indentLevel={0} />)}</ul>;
    $[15] = activeValues;
    $[16] = group.options;
    $[17] = isOpen;
    $[18] = toggleValue;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t6 || $[21] !== t7) {
    t8 = <div className="border-b border-gray-m2 t2-dark:border-gray-m7 py-4 px-5 bg-white rounded-lg mt-4">{t6}{t7}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
const FilterCheckboxTree = t0 => {
  const $ = _c(16);
  const {
    option,
    activeValues,
    onToggle,
    indentLevel
  } = t0;
  let t1;
  if ($[0] !== activeValues || $[1] !== option.value) {
    t1 = activeValues.includes(option.value);
    $[0] = activeValues;
    $[1] = option.value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== indentLevel || $[4] !== onToggle || $[5] !== option || $[6] !== t1) {
    t2 = <FilterCheckbox option={option} checked={t1} onToggle={onToggle} indentLevel={indentLevel} />;
    $[3] = indentLevel;
    $[4] = onToggle;
    $[5] = option;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== activeValues || $[9] !== indentLevel || $[10] !== onToggle || $[11] !== option.children) {
    t3 = option.children?.map(child => <FilterCheckboxTree key={child.value} option={child} activeValues={activeValues} onToggle={onToggle} indentLevel={indentLevel + 1} />);
    $[8] = activeValues;
    $[9] = indentLevel;
    $[10] = onToggle;
    $[11] = option.children;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
export const FilterSidebar = t0 => {
  const $ = _c(19);
  const {
    filterGroups,
    activeFilters,
    onFilterChange,
    onClearAll,
    labels
  } = t0;
  const hasActiveFilters = Object.values(activeFilters).some(_temp);
  const t1 = labels?.labelForFilters || "Filters";
  let t2;
  if ($[0] !== t1) {
    t2 = <h3 className="text-[22px] font-bold text-gray-m9 t2-dark:text-white m-0">{t1}</h3>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== hasActiveFilters || $[3] !== labels?.labelForClearAll || $[4] !== onClearAll) {
    t3 = hasActiveFilters && <button className="text-blue-trmb t2-dark:text-yellow-m10 text-xl font-semibold cursor-pointer bg-transparent border-none p-0 hover:underline" onClick={onClearAll}>{labels?.labelForClearAll || "Clear All"}</button>;
    $[2] = hasActiveFilters;
    $[3] = labels?.labelForClearAll;
    $[4] = onClearAll;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-center justify-between pb-6 border-b border-gray-m2 t2-dark:border-gray-m7">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== activeFilters || $[10] !== filterGroups || $[11] !== onFilterChange) {
    let t6;
    if ($[13] !== activeFilters || $[14] !== onFilterChange) {
      t6 = group => <FilterGroupSection key={group.id} group={group} activeValues={activeFilters[group.id] || []} onFilterChange={onFilterChange} />;
      $[13] = activeFilters;
      $[14] = onFilterChange;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    t5 = filterGroups.map(t6);
    $[9] = activeFilters;
    $[10] = filterGroups;
    $[11] = onFilterChange;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[16] !== t4 || $[17] !== t5) {
    t6 = <aside className="w-full lg:w-[290px] lg:min-w-[290px] lg:pr-6">{t4}{t5}</aside>;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
function _temp(v) {
  return v.length > 0;
}